import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { TypedTokenStorageService } from '../token/typed-token-storage.service';
import { AUTH_CONFIG } from 'src/app/constants/auth.constants';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private tokenRenewalPromise: Promise<string> | null = null;

  constructor(
    private authService: MsalService,
    private typedTokenStorage: TypedTokenStorageService
  ) {}
  private async acquireTokenWithDebounce(): Promise<string> {
    if (this.tokenRenewalPromise) {
      return this.tokenRenewalPromise;
    }

    this.tokenRenewalPromise = this._performTokenRenewal();

    try {
      return await this.tokenRenewalPromise;
    } finally {
      this.tokenRenewalPromise = null;
    }
  }

  private async _performTokenRenewal(): Promise<string> {
    const accounts = this.authService.instance.getAllAccounts();
    if (!accounts.length) {
      throw new Error('No accounts found');
    }

    const request = {
      scopes: AUTH_CONFIG.SCOPES,
      account: accounts[0],
    };

    try {
      const response = await this.authService.instance.acquireTokenSilent(request);
      
      this.typedTokenStorage.saveAllTokens(
        response.accessToken,
        response.idToken
      );
      
      return response.accessToken;
    } catch (silentError) {
      try {
        const popupResponse = await this.authService.instance.acquireTokenPopup(request);
        
        this.typedTokenStorage.saveAllTokens(
          popupResponse.accessToken,
          popupResponse.idToken
        );
        
        return popupResponse.accessToken;
      } catch (popupError) {
        throw popupError;
      }
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.typedTokenStorage.getAccessToken();
    const tokenTimestamp = this.typedTokenStorage.getTokenTimestamp();

    if (!token) {
      return next.handle(req);
    }

    if (tokenTimestamp && Date.now() - tokenTimestamp < AUTH_CONFIG.TOKEN_REUSE_WINDOW_MS) {
      return next.handle(this.cloneRequestWithToken(req, token));
    }
    
    return from(this.acquireTokenWithDebounce()).pipe(
      switchMap((newToken) => next.handle(this.cloneRequestWithToken(req, newToken))),
      catchError(() => next.handle(this.cloneRequestWithToken(req, token)))
    );
  }

  private cloneRequestWithToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    const cleanedToken = token.replace(/^"|"$/g, '');
    return req.clone({
      headers: req.headers.set('Authorization', `Bearer ${cleanedToken}`)
    });
  }
}
